import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api, { resolveAvatarUrl } from '../api';
import LogoMark from './LogoMark';
import Toast from './Toast';
import BlobField from './BlobField';
import ImpersonationBanner from './ImpersonationBanner';
import ShareClanModal from './ShareClanModal';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * AppShell — shared layout for every authenticated screen.
 * - Header (mobile & desktop): logo + title + notifications + avatar menu
 * - Desktop-only inline top nav
 * - Mobile-only fixed bottom tab bar
 *
 * IMPORTANT (per handoff doc §4 bug-guard): the mobile tab bar and desktop
 * header-nav MUST ship together. Do not remove one without the other.
 */
export default function AppShell({ children, activeTab = 'home' }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [avatarImgOk, setAvatarImgOk] = useState(true);
  const [toast, setToast] = useState(null);
  const [myClan, setMyClan] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // --- Mobile tab-bar auto-hide (Trade screen only) --------------------
  // On the /trade route the tab-bar tucks itself away so the arcade chart
  // + trade panel get every pixel. User re-veals via (a) tap the thin
  // "handle" bar always visible at the bottom, or (b) swipe up from the
  // bottom edge (Instagram-reels style). Anything BUT /trade shows the
  // tab-bar as normal.
  const isTradeScreen = location.pathname === '/trade' || location.pathname.startsWith('/trade/');
  const [tabbarRevealed, setTabbarRevealed] = useState(false);
  // Reset the reveal state on every route change so tucking is automatic
  // when re-entering the trade screen.
  useEffect(() => { setTabbarRevealed(false); }, [location.pathname]);
  // Swipe-up gesture on the bottom 24px band of the viewport.
  useEffect(() => {
    if (!isTradeScreen) return undefined;
    let startY = null;
    const onTouchStart = (e) => {
      const t = e.touches[0]; if (!t) return;
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      // Only track swipes that begin in the bottom 40px of the viewport so we
      // don't fight with normal in-page scrolling.
      if (viewportH - t.clientY <= 40) { startY = t.clientY; }
    };
    const onTouchMove = (e) => {
      if (startY == null) return;
      const t = e.touches[0]; if (!t) return;
      if (startY - t.clientY > 24) { setTabbarRevealed(true); startY = null; }
    };
    const onTouchEnd = () => { startY = null; };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [isTradeScreen]);

  // Dismiss the revealed tabbar on outside-tap, Escape key, or a swipe-down
  // gesture that starts on the tabbar itself. Without this the user is stuck
  // once they open it — the previous handle only worked in the tucked state.
  const tabbarRef = useRef(null);
  useEffect(() => {
    if (!isTradeScreen || !tabbarRevealed) return undefined;
    const onDown = (e) => {
      if (tabbarRef.current && !tabbarRef.current.contains(e.target)) {
        setTabbarRevealed(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setTabbarRevealed(false); };
    let swipeStartY = null;
    const onTStart = (e) => {
      const t = e.touches[0]; if (!t) return;
      if (tabbarRef.current && tabbarRef.current.contains(e.target)) {
        swipeStartY = t.clientY;
      }
    };
    const onTMove = (e) => {
      if (swipeStartY == null) return;
      const t = e.touches[0]; if (!t) return;
      if (t.clientY - swipeStartY > 24) { setTabbarRevealed(false); swipeStartY = null; }
    };
    const onTEnd = () => { swipeStartY = null; };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    document.addEventListener('keydown', onKey);
    window.addEventListener('touchstart', onTStart, { passive: true });
    window.addEventListener('touchmove', onTMove, { passive: true });
    window.addEventListener('touchend', onTEnd);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('touchstart', onTStart);
      window.removeEventListener('touchmove', onTMove);
      window.removeEventListener('touchend', onTEnd);
    };
  }, [isTradeScreen, tabbarRevealed]);

  // Retry the avatar load whenever the user or their avatar URL changes.
  useEffect(() => { setAvatarImgOk(true); }, [user?.id, user?.avatar]);

  // Fetch current user's Clan (if any) to swap 'Start my Clan' → 'Manage my Clan'
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api.get('/clans/mine')
      .then(({ data }) => { if (!cancelled) setMyClan(data.clan); })
      .catch(() => { if (!cancelled) setMyClan(null); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const goto = (path) => {
    setAvatarOpen(false);
    navigate(path);
  };
  const showComingSoon = (feature) => {
    setToast({ msg: `🚧 ${feature} — coming soon!`, tone: 'info' });
    setAvatarOpen(false);
  };

  // Close avatar dropdown on outside click / Escape.
  const avatarWrapRef = useRef(null);
  useEffect(() => {
    if (!avatarOpen) return;
    const onDown = (e) => {
      if (avatarWrapRef.current && !avatarWrapRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setAvatarOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [avatarOpen]);


  // Poll unread notifications count (light-weight)
  const [unreadCount, setUnreadCount] = useState(0);
  const [dmUnread, setDmUnread] = useState(0);
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const fetchCounts = () => {
      api.get('/notifications/unread-count')
        .then(({ data }) => { if (!cancelled) setUnreadCount(data.count || 0); })
        .catch(() => {});
      api.get('/dm/unread-count')
        .then(({ data }) => { if (!cancelled) setDmUnread(data.unread_threads || 0); })
        .catch(() => {});
    };
    fetchCounts();
    const timer = setInterval(fetchCounts, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user?.id]);

  const initials = (user?.display_name || user?.handle || 'U')
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  // Main nav — user-approved order (2026-02): Home · Active Clans · Trade ·
  // Bounties · Perks · Wallet. Create/Manage Clan lives in the avatar menu
  // (between Account Settings and Launches) so the main bar stays focused on
  // fan-facing browse/trade tools.
  const clanNavItem = myClan
    ? { key: 'clan-cta', label: t('nav.manageClan'), to: `/clan/${myClan.handle}/feed`, onClick: null }
    : { key: 'clan-cta', label: t('nav.createClan'), to: '/create-clan', onClick: null };

  const navItems = [
    { key: 'home', label: t('nav.home'), to: '/home', onClick: null },
    { key: 'clans', label: t('nav.activeClans'), to: '/my-clans', onClick: null },
    { key: 'trade', label: t('nav.trade'), to: '/trade', onClick: null },
    { key: 'bounties', label: t('nav.bounties'), to: '/bounties', onClick: null },
    { key: 'perks', label: t('nav.perks'), to: '/perks', onClick: null },
    { key: 'wallet', label: t('nav.wallet'), to: '/wallet', onClick: null },
  ];

  return (
    <div className="app-shell">
      <BlobField />
      <ImpersonationBanner />

      <header className={`shell-header${isTradeScreen ? ' shell-header-trade' : ''}`}>
        <div className="shell-header-inner">
          <Link to="/home" className="shell-logo" aria-label="Clanster home" data-testid="shell-logo">
            <LogoMark size={38} />
            <span className="shell-brand">Clanster</span>
          </Link>

          <nav className="shell-nav" aria-label="Primary">
            {navItems.map((it) => (
              it.to ? (
                <NavLink
                  key={it.key}
                  to={it.to}
                  className={({ isActive }) => `shell-nav-link${isActive || activeTab === it.key ? ' active' : ''}`}
                  data-testid={`nav-${it.key}`}
                >
                  {it.label}
                </NavLink>
              ) : (
                <button
                  key={it.key}
                  type="button"
                  className={`shell-nav-link${activeTab === it.key ? ' active' : ''}`}
                  onClick={it.onClick}
                  data-testid={`nav-${it.key}`}
                >
                  {it.label}
                </button>
              )
            ))}
            {user?.role === 'admin' && (
              <NavLink
                to="/admin"
                className={({ isActive }) => `shell-nav-link admin-nav-link-shell${isActive ? ' active' : ''}`}
                data-testid="nav-admin"
              >
                Admin
              </NavLink>
            )}
          </nav>

          <div className="shell-spacer" />

          <button
            className="shell-icon-btn"
            aria-label="Messages"
            onClick={() => navigate('/messages')}
            data-testid="dm-btn"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H8l-4 3V7a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
            {dmUnread > 0 && (
              <span className="shell-notif-badge" data-testid="dm-badge">
                {dmUnread > 9 ? '9+' : dmUnread}
              </span>
            )}
          </button>

          <button
            className="shell-icon-btn"
            aria-label="Notifications"
            onClick={() => navigate('/notifications')}
            data-testid="notif-btn"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M6 10a6 6 0 1112 0c0 4 1.5 5.5 1.5 5.5H4.5S6 14 6 10z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 19a2 2 0 004 0" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            {unreadCount > 0 && (
              <span className="shell-notif-badge" data-testid="notif-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <div className="avatar-menu-wrap" ref={avatarWrapRef}>
            <button
              className="shell-avatar-btn"
              onClick={() => setAvatarOpen((v) => !v)}
              aria-label="Account menu"
              data-testid="avatar-btn"
            >
              <div className="shell-avatar-ring">
                <div className="shell-avatar-inner">
                  {(() => {
                    const src = resolveAvatarUrl(user?.avatar);
                    if (src && avatarImgOk) {
                      return <img src={src} alt="" onError={() => setAvatarImgOk(false)} />;
                    }
                    return <span>{initials}</span>;
                  })()}
                </div>
              </div>
            </button>
            {avatarOpen && (
              <>
                <div className="avatar-menu-scrim" onClick={() => setAvatarOpen(false)} />
                <div className="avatar-menu" data-testid="avatar-menu">
                  <div className="avatar-menu-header">
                    <div className="amh-name">{user?.display_name}</div>
                    <div className="amh-handle">@{user?.handle}</div>
                  </div>
                  {myClan?.handle && (
                    <button
                      className="avatar-menu-item avatar-menu-item-highlight"
                      onClick={() => { setAvatarOpen(false); setShareOpen(true); }}
                      data-testid="menu-share-clan"
                    >
                      {t('avatarMenu.shareMyClan')}
                      <span className="menu-item-sub">{t('avatarMenu.shareMyClanSub')}</span>
                    </button>
                  )}
                  <button className="avatar-menu-item" onClick={() => goto('/profile')} data-testid="menu-profile">
                    {t('avatarMenu.editProfile')}
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/settings')} data-testid="menu-settings">
                    {t('avatarMenu.accountSettings')}
                    <span className="menu-item-sub">{t('avatarMenu.accountSettingsSub')}</span>
                  </button>
                  <button
                    className="avatar-menu-item"
                    onClick={() => goto(clanNavItem.to)}
                    data-testid={myClan ? 'menu-manage-clan' : 'menu-create-clan'}
                  >
                    {myClan ? t('avatarMenu.manageClan') : t('avatarMenu.createClan')}
                    <span className="menu-item-sub">
                      {myClan ? t('avatarMenu.manageClanSub') : t('avatarMenu.createClanSub')}
                    </span>
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/launches')} data-testid="menu-launches">
                    {t('avatarMenu.launches')}
                    <span className="menu-item-sub">{t('avatarMenu.launchesSub')}</span>
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/verify')} data-testid="menu-verify">
                    {t('avatarMenu.getVerified')}
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/pulse')} data-testid="menu-pulse">
                    {t('avatarMenu.pulse')}
                    <span className="menu-item-sub">{t('avatarMenu.pulseSub')}</span>
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/leaderboards')} data-testid="menu-leaderboard">
                    {t('avatarMenu.leaderboard')}
                    <span className="menu-item-sub">{t('avatarMenu.leaderboardSub')}</span>
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/bookmarks')} data-testid="menu-bookmarks">
                    {t('avatarMenu.bookmarks')}
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/referrals')} data-testid="menu-referrals">
                    {t('avatarMenu.referFriends')}
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/preferences')} data-testid="menu-preferences">
                    {t('avatarMenu.preferences')}
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/help')} data-testid="menu-help">
                    {t('avatarMenu.helpCentre')}
                  </button>
                  <button className="avatar-menu-item" onClick={() => goto('/legal')} data-testid="menu-legal">
                    {t('avatarMenu.legal')}
                    <span className="menu-item-sub">{t('avatarMenu.legalSub')}</span>
                  </button>
                  <button
                    className="avatar-menu-item"
                    onClick={() => setLangOpen(true)}
                    data-testid="menu-language"
                  >
                    {t('avatarMenu.language')}
                    <span className="menu-item-sub">{t('avatarMenu.languageSub')}</span>
                  </button>
                  {user?.role === 'admin' && (
                    <button className="avatar-menu-item" onClick={() => goto('/admin')} data-testid="menu-admin">
                      {t('avatarMenu.adminPanel')}
                    </button>
                  )}
                  <button
                    className="avatar-menu-item danger"
                    data-testid="menu-signout"
                    onClick={async () => {
                      setAvatarOpen(false);
                      await logout();
                      navigate('/login', { replace: true });
                    }}
                  >
                    {t('common.signOut')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="shell-main">{children}</main>

      <nav
        ref={tabbarRef}
        className={`shell-tabbar${isTradeScreen && !tabbarRevealed ? ' tucked' : ''}${isTradeScreen && tabbarRevealed ? ' revealed' : ''}`}
        aria-label="Mobile tabs"
        data-testid="shell-tabbar"
      >
        {isTradeScreen && tabbarRevealed && (
          <button
            type="button"
            className="shell-tabbar-close"
            onClick={() => setTabbarRevealed(false)}
            aria-label="Hide navigation"
            data-testid="shell-tabbar-close"
          >
            <span className="shell-tabbar-close-grip" aria-hidden="true" />
          </button>
        )}
        {navItems.map((it) => (
          it.to ? (
            <NavLink
              key={it.key}
              to={it.to}
              className={({ isActive }) => `shell-tab${isActive || activeTab === it.key ? ' active' : ''}`}
              data-testid={`tab-${it.key}`}
              onClick={() => { if (isTradeScreen) setTabbarRevealed(false); }}
            >
              <TabIcon k={it.key} />
              <span>{it.label.replace(/🚀\s*/, '').replace(/🏆\s*/, '').replace(/🎯\s*/, '').replace(/🎁\s*/, '')}</span>
            </NavLink>
          ) : (
            <button
              key={it.key}
              type="button"
              className={`shell-tab${activeTab === it.key ? ' active' : ''}`}
              onClick={() => { it.onClick?.(); if (isTradeScreen) setTabbarRevealed(false); }}
              data-testid={`tab-${it.key}`}
            >
              <TabIcon k={it.key} />
              <span>{it.label.replace(/🚀\s*/, '').replace(/🏆\s*/, '').replace(/🎯\s*/, '').replace(/🎁\s*/, '')}</span>
            </button>
          )
        ))}
      </nav>

      {/* Tap-handle for the tucked tabbar — kept OUTSIDE .shell-tabbar because
          the tucked state uses `transform: translateY(...)` on the nav, and
          any `position: fixed` descendants of a transformed element get
          positioned relative to the transformed ancestor (a known CSS spec
          quirk) — placing the handle above the viewport. As a sibling of
          the tabbar it stays anchored to the actual viewport bottom. */}
      {isTradeScreen && !tabbarRevealed && (
        <button
          type="button"
          className="shell-tabbar-handle"
          onClick={() => setTabbarRevealed(true)}
          aria-label="Show navigation"
          data-testid="shell-tabbar-handle"
        >
          <span className="shell-tabbar-handle-grip" aria-hidden="true" />
        </button>
      )}

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
      <ShareClanModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        handle={myClan?.handle}
      />
      <LanguageSwitcher open={langOpen} onClose={() => setLangOpen(false)} />
    </div>
  );
}

function TabIcon({ k }) {
  const props = { viewBox: '0 0 24 24', fill: 'none', width: 24, height: 24 };
  const sw = 2.3;
  switch (k) {
    case 'home':
      return (
        <svg {...props}><path d="M4 11l8-6 8 6v8a1 1 0 01-1 1h-4v-6H9v6H5a1 1 0 01-1-1v-8z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" /></svg>
      );
    case 'launches':
      return (
        <svg {...props}><path d="M12 2c3 3 4 7 3 11l-3 3-3-3c-1-4 0-8 3-11z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" /><circle cx="12" cy="9" r="1.6" fill="currentColor" /><path d="M9 15l-2 4M15 15l2 4" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" /></svg>
      );
    case 'pulse':
      return (
        <svg {...props}><path d="M4 18V10M11 18V6M18 18v-5" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" /></svg>
      );
    case 'leaders':
      return (
        <svg {...props}><path d="M8 21h8M12 17v4M6 4h12v3a6 6 0 01-12 0V4z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" /><path d="M6 6H4a2 2 0 000 4h2M18 6h2a2 2 0 010 4h-2" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" /></svg>
      );
    case 'clans':
      return (
        <svg {...props}><circle cx="8" cy="9" r="3" stroke="currentColor" strokeWidth={sw} /><circle cx="16" cy="9" r="3" stroke="currentColor" strokeWidth={sw} /><path d="M2 19c0-3 2.5-5 6-5s6 2 6 5M10 19c0-3 2.5-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" /></svg>
      );
    case 'clan-cta':
      return (
        <svg {...props}><path d="M3 9l3 8h12l3-8-4 3-5-6-5 6-4-3z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" /></svg>
      );
    case 'wallet':
      return (
        <svg {...props}><rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth={sw} /><path d="M3 10h18" stroke="currentColor" strokeWidth={sw} /></svg>
      );
    case 'bounties':
      return (
        <svg {...props}><circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth={sw} /><circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth={sw} /><circle cx="12" cy="12" r="1.4" fill="currentColor" /></svg>
      );
    case 'perks':
      return (
        <svg {...props}>
          <rect x="3" y="9" width="18" height="12" rx="1.5" stroke="currentColor" strokeWidth={sw} />
          <path d="M3 13h18" stroke="currentColor" strokeWidth={sw} />
          <path d="M12 9v12" stroke="currentColor" strokeWidth={sw} />
          <path d="M12 9c-1.5-2.5-4.5-3-5.5-1.5S6.5 9 12 9zm0 0c1.5-2.5 4.5-3 5.5-1.5S17.5 9 12 9z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" />
        </svg>
      );
    case 'trade':
      // Rocket silhouette — matches the RocketChart theme on the /trade
      // screen so the tab-bar icon reads as "the trading arcade".
      return (
        <svg {...props}>
          <path d="M12 2c3 3 4.5 7 3.5 12l-3.5 3-3.5-3c-1-5 .5-9 3.5-12z" stroke="currentColor" strokeWidth={sw} strokeLinejoin="round" />
          <circle cx="12" cy="10" r="1.8" fill="currentColor" />
          <path d="M8.5 17l-2.5 4M15.5 17l2.5 4" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}
